import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  StatusCell,
  PaginationTable,
  DateCell,
  DefaultButton,
  BaseSelectField,
  BasicModal,
  Calendar,
  RowLink,
  PaymentTag,
} from 'common/components';
import { withTheme } from 'common/styling/theme';
import Checkbox from 'backoffice/partnership/_components/Checkbox';

import { createBackOfficePaymentExport, getPaymentMetadataKeys } from '../../_redux';

import { staticStyles, getDynamicStyles } from './style';

const BONUS_ABUSER_TAG_NAME = 'Bonus Abuser';

const PaymentsTable = ({
  data,
  allowManagePaymentsExports,
  exportButtonCaptionSlug,
  isExportPaymentBeingCreated,
  createBackOfficePaymentExport,
  onRowClick,
  getList,
  loading,
  history,
  type,
  location,
  params,
  theme,
  intl,
  hasPaymentOperationManagers,
  getPaymentMetadataKeys,
  metadataKeys,
  disableLinks,
  hideColumns = [],
  hideMetadataFilter = false,
}) => {
  const slugsMap = {
    deposits: 'deposit',
    withdrawals: 'withdraw',
  };

  const [onlyExecutedChecked, setOnlyExecutedChecked] = useState(false);

  const handeToggleOnlyExecutedField = () => {
    setOnlyExecutedChecked(checked => !checked);
  };

  const pushToExportsFunc = useCallback(() => history.push(`/backoffice/${type}/exports`), [type]);
  const exportsType = useMemo(() => {
    if (type === 'deposits') {
      return 'deposit_export';
    }
    return 'withdraw_export';
  }, []);

  const savedMetada = JSON.parse(localStorage.getItem('savedColumns')) || [];

  const [selectedMetadata, setSelectedMetadata] = useState(savedMetada);

  const metadataOptions = metadataKeys.map(item => ({ label: item, value: item }));

  const handleMetadataChange = values => {
    if (values) {
      setSelectedMetadata([...values]);
      localStorage.setItem('savedColumns', JSON.stringify([...values]));

      return;
    }

    setSelectedMetadata([]);
    localStorage.setItem('savedColumns', JSON.stringify([]));
  };

  const extraColumns = selectedMetadata.map(item => ({
    key: item.value,
    Cell: ({ original: { metadata } }) => <div>{metadata[item.value] || '-'}</div>,
  }));

  const getRowLink = id => {
    if (disableLinks) return '';
    let link = '';

    if (type === 'deposits') {
      link = `/backoffice/${type}/${id}/`;
    } else {
      link = `/backoffice/${type}/list/${id}/`;
    }

    return link;
  };

  /* eslint-disable */
  const columns = useMemo(
    () => {
      const allColumns = [
        {
          key: 'boId',
          accessor: 'id',
          Cell: ({ original: { id } }) => (
            <RowLink link={getRowLink(id)}>
              <span className="id">{id}</span>
            </RowLink>
          ),
        },
        {
          key: 'justEmail',
          accessor: 'user__email',
          Cell: ({ original: { user, id } }) => (
            <span className="PaymentsTable__EmailCell">
              <RowLink link={getRowLink(id)} isPreviewCell>
                <span className="PaymentsTable__EmailText">{user.email}</span>
              </RowLink>
              {(user.userTags || []).some(tag => tag.name === BONUS_ABUSER_TAG_NAME) && (
                <span className="PaymentsTable__BonusAbuserTag">{BONUS_ABUSER_TAG_NAME}</span>
              )}
            </span>
          ),
          isPreviewCell: true,
        },
        {
          key: 'boAccount',
          accessor: 'account',
          Cell: ({ original: { account, id } }) => (
            <RowLink link={getRowLink(id)}>
              <span className="account">{account?.login ?? intl.formatMessage({ id: 'boWallet' })}</span>
            </RowLink>
          ),
        },
        {
          key: 'boAmount',
          Cell: ({ original: { amount, requestedAmount, id } }) => (
            <RowLink link={getRowLink(id)} isPreviewCell>
              <span className="amount">
                {(requestedAmount ? requestedAmount.amount : amount.amount)}
                {(requestedAmount ? requestedAmount.currency : amount.currency)}
              </span>
            </RowLink>
          ),
          isPreviewCell: true,
        },
        {
          ...(data.results && data.results.length > 0 && 'requestedAmount' in data.results[0]
            ? {
          key: 'boReceivedAmount',
          Cell: ({ original: { amount, status, requestedAmount, isReceived, id } }) => (
            <RowLink link={getRowLink(id)}>
              <span className="received-amount">
                {(isReceived || status === 'deposited') ? amount.amount : '-'}
                {(isReceived || status === 'deposited') ? amount.currency : ''}
              </span>
            </RowLink>
          ),
              }
            : {}),
        },
        {
          key: 'justRequestedCommission',
          Cell: ({ original: { requestedCommission, commission, id } }) => (
            <RowLink link={getRowLink(id)}>
              <span className="requestedCommission">
                {(requestedCommission ? requestedCommission.amount : commission.amount)}
                {(requestedCommission ? requestedCommission.currency : commission.currency)}
              </span>
            </RowLink>
          ),
        },
        {
          ...(data.results && data.results.length > 0 && 'requestedCommission' in data.results[0]
            ? {
              key: 'justReceivedCommission',
              Cell: ({ original: { commission, status, requestedCommission, isReceived, id } }) => (
                <RowLink link={getRowLink(id)}>
              <span className="received-commission">
                {(isReceived || status === 'deposited') ? commission.amount : '-'}
                {(isReceived || status === 'deposited') ? commission.currency : ''}
              </span>
                </RowLink>
              ),
            }
            : {}),
        },
        ...(hasPaymentOperationManagers
          ? [
              {
                key: 'justManager',
                Cell: ({ original: { manager, id } }) => (
                  <RowLink link={getRowLink(id)}>
                    <div>{manager ? `${manager.firstName} ${manager.lastName}` : '-'}</div>
                  </RowLink>
                ),
              },
            ]
          : []),
        {
          key: 'justConvertationCourse',
          Cell: ({ original: { exchangeString, id } }) => (
            <RowLink link={getRowLink(id)}>
              <span className="convertation-course">{exchangeString}</span>
            </RowLink>
          ),
        },
        {
          key: 'boPaymentSystem',
          Cell: ({ original: { paymentSystemSlug, id } }) => (
            <RowLink link={getRowLink(id)}>
              <span className="payment-system-slug">{paymentSystemSlug}</span>
            </RowLink>
          ),
        },
        {
          key: 'boPaymentMethod',
          Cell: ({ original: { paymentMethodTitle, id } }) => (
            <RowLink link={getRowLink(id)}>
              <span className="payment-method-slug">{paymentMethodTitle}</span>
            </RowLink>
          ),
        },
        {
          key: 'boCreated',
          accessor: 'created',
          Cell: ({ original: { created, id } }) => (
            <RowLink link={getRowLink(id)} isPreviewCell>
              <DateCell value={created} />
            </RowLink>
          ),
          isPreviewCell: true,
        },
        {
          key: 'boStatus',
          accessor: '_status',
          Cell: ({ original: { status, id } }) => (
            <RowLink link={getRowLink(id)} isPreviewCell>
              <div className="status">
                <StatusCell statusCode={status} />
              </div>
            </RowLink>
          ),
          isPreviewCell: true,
        },
        {
          key: 'justPaymentTags',
          Cell: ({ original: { tags, id } }) => (
            <RowLink link={getRowLink(id)}>
              <div className="PaymentsTable__tags">
                {tags.length
                  ? tags
                      .slice(0, 2)
                      .map(tag => <PaymentTag key={tag.id} title={tag.name} color={tag.color} isShortWidth />)
                  : '-'}
                {tags.length > 2 && (
                  <FormattedMessage id="justMoreTags" values={{ count: tags.length - 2 }}>
                    {txt => <span className="PaymentsTable__tags__caption">{txt}</span>}
                  </FormattedMessage>
                )}
              </div>
            </RowLink>
          ),
        },
        ...extraColumns,
      ];

      return allColumns.filter(col => col.key && !hideColumns.includes(col.key));
    },
    [selectedMetadata, hideColumns]
  );
  /* eslint-enable */

  const rowProps = (state, rowInfo) => ({
    onClick: () => onRowClick(rowInfo.original.id),
  });

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const [isExportMenuModalOpen, setIsExportMenuModalOpen] = useState(false);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
  const [exportExt, setExportExt] = useState(null);

  const [startDate, setStartDate] = useState(moment().add(-7, 'day').startOf('day'));

  const [endDate, setEndDate] = useState(moment().startOf('day'));

  const hadleChangeDate = (startDate, endDate) => {
    setStartDate(startDate * 1000);
    setEndDate(endDate * 1000);
    setSelectedTimePeriod(
      `${moment(startDate * 1000).format('YYYY-MM-DD')}_${moment(endDate * 1000).format('YYYY-MM-DD')}`
    );
  };

  useEffect(() => {
    if (isExportMenuModalOpen) {
      setSelectedTimePeriod(`${moment(startDate).format('YYYY-MM-DD')}_${moment(endDate).format('YYYY-MM-DD')}`);
    }
  }, [isExportMenuModalOpen]);

  useEffect(() => {
    // 判断是否在 CRM finances 页面
    const isFinancesPage = location.pathname.includes('/crm/finances');
    getPaymentMetadataKeys(slugsMap[type], isFinancesPage);
  }, [getPaymentMetadataKeys]);

  return (
    <div className="PaymentsTable">
      <div className="PaymentsTable__buttons">
        <div className="PaymentsTable__buttons__metadata">
          {!hideMetadataFilter && metadataKeys.length > 0 && (
            <BaseSelectField
              name="metadata"
              value={selectedMetadata}
              isMulti
              textId="boChooseMetadataColumns"
              options={metadataOptions}
              onChange={handleMetadataChange}
            />
          )}
        </div>
        {allowManagePaymentsExports && exportButtonCaptionSlug && (
          <div className="PaymentsTable__export-buttons">
            <DefaultButton
              type="button"
              textId={exportButtonCaptionSlug}
              onClick={() => history.push(`/backoffice/${type}/exports`)}
            />
            <div className="PaymentsTable__export-buttons--select">
              <BaseSelectField
                name="import"
                textId="justExport"
                options={[
                  { value: 'xlsx', label: 'Export in xlsx' },
                  { value: 'csv', label: 'Export in csv' },
                ]}
                onChange={option => {
                  setExportExt(option.value);
                  setIsExportMenuModalOpen(true);
                }}
                disabled={isExportPaymentBeingCreated}
              />
            </div>
          </div>
        )}
      </div>

      <PaginationTable
        data={data}
        loading={loading}
        columns={columns}
        onRowClick={onRowClick}
        getList={getList}
        location={location}
        history={history}
        params={params}
        getTrProps={rowProps}
      />
      <BasicModal
        captionId="selectTimePeriodOfExport"
        isOpen={isExportMenuModalOpen}
        onRequestClose={() => setIsExportMenuModalOpen(false)}>
        <Calendar
          setInMiddle
          daysLimit={31}
          setCorrectLastDay
          onChangeFunc={hadleChangeDate}
          defaultStartDate={startDate}
          defaultEndDate={endDate}
        />
        <div className="PaymentsTable--onlyExecutedField">
          <Checkbox checked={onlyExecutedChecked} onClick={handeToggleOnlyExecutedField} />
          <FormattedMessage id="justSetOnlyExecuted" />
        </div>
        <DefaultButton
          textId="justExport"
          type="button"
          filled
          disabled={!selectedTimePeriod}
          onClick={() => {
            setIsExportMenuModalOpen(false);
            createBackOfficePaymentExport({
              exportsType,
              pushToExportsFunc,
              ext: exportExt,
              timePeriod: selectedTimePeriod,
              onlyExecuted: onlyExecutedChecked,
            });
          }}
        />
      </BasicModal>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

PaymentsTable.propTypes = {
  onRowClick: PropTypes.func.isRequired,
  allowManagePaymentsExports: PropTypes.bool.isRequired,
  isExportPaymentBeingCreated: PropTypes.bool.isRequired,
  createBackOfficePaymentExport: PropTypes.func.isRequired,
  exportButtonCaptionSlug: PropTypes.string,
  data: PropTypes.object.isRequired,
  type: PropTypes.string,
  getList: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  hasPaymentOperationManagers: PropTypes.bool.isRequired,
  getPaymentMetadataKeys: PropTypes.func.isRequired,
  theme: PropTypes.object,
  metadataKeys: PropTypes.array,
};

PaymentsTable.defaultProps = {
  exportButtonCaptionSlug: '',
  type: '',
  metadataKeys: [],
  theme: {},
};

export default connect(
  state => ({
    isExportPaymentBeingCreated: state.backoffice.payments.isExportPaymentBeingCreated,
    metadataKeys: state.backoffice.payments.metadataKeys,
  }),
  {
    createBackOfficePaymentExport: ({ exportsType, pushToExportsFunc, ext, timePeriod, onlyExecuted }) =>
      createBackOfficePaymentExport.request({ exportsType, pushToExportsFunc, ext, timePeriod, onlyExecuted }),
    getPaymentMetadataKeys: captionSlug => getPaymentMetadataKeys.request(captionSlug),
  }
)(injectIntl(withTheme()(PaymentsTable)));
export { PaymentsTable };
